import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, BellRing, Share, SquarePlus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PushSubscriptionList } from '@up2d8/contracts';
import { Badge, Button, Card, CardTitle } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { usePush } from '../../shared/use-push';
import { isNativeApp } from '../../shared/native';
import { formatDateTime } from '../../shared/format';

/**
 * De kaart waarmee een medewerker meldingen aanzet.
 *
 * De uitleg staat vóór de knop en niet erna, en dat is de hele opzet van dit ding: een
 * browserprompt die uit de lucht komt vallen, wordt weggeklikt — en een weggeklikte prompt komt
 * niet terug zonder dat iemand in de instellingen van zijn telefoon duikt. Eerst zeggen wat je
 * krijgt, dan vragen.
 *
 * Op een iPhone werkt web push alleen als de app op het beginscherm staat (iOS 16.4+). Dat is
 * geen fout die je verbergt maar een instructie die je geeft, met de twee stappen erbij.
 */
export function PushCard({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const push = usePush();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);

  const devices = useQuery({
    queryKey: ['push-subscriptions'],
    enabled: !compact,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/push/subscriptions');
      return (data ?? { items: [] }) as PushSubscriptionList;
    },
  });

  const onEnable = async (): Promise<void> => {
    const result = await push.enable();
    setMessage(
      result === 'denied' ? (isNativeApp() ? t('push.nativeDenied') : t('push.denied')) : null,
    );
    await queryClient.invalidateQueries({ queryKey: ['push-subscriptions'] });
  };

  const onDisable = async (): Promise<void> => {
    await push.disable();
    await queryClient.invalidateQueries({ queryKey: ['push-subscriptions'] });
  };

  if (push.state === 'on' && compact) return null;

  // In de app houdt de telefoon de meldingen tegen, niet de browser (MOB-1).
  const deniedText = isNativeApp() ? t('push.nativeDenied') : t('push.denied');

  return (
    <Card compact={compact}>
      <CardTitle>
        <span className="inline-flex items-center gap-2">
          {push.state === 'on' ? (
            <BellRing aria-hidden className="h-5 w-5 text-brand-primary" />
          ) : (
            <Bell aria-hidden className="h-5 w-5 text-text-muted" />
          )}
          {t('push.title')}
        </span>
      </CardTitle>

      {push.state === 'ios-install-needed' ? (
        // De enige weg op een iPhone: eerst op het beginscherm. Met de twee stappen erbij,
        // want niemand vindt "Zet op beginscherm" zonder aanwijzing.
        <div className="mt-element flex flex-col gap-2 text-content text-text-secondary">
          <p>{t('push.iosIntro')}</p>
          <ol className="flex flex-col gap-1.5">
            <li className="flex items-center gap-2">
              <Share aria-hidden className="h-4 w-4 text-brand-primary" />
              {t('push.iosStep1')}
            </li>
            <li className="flex items-center gap-2">
              <SquarePlus aria-hidden className="h-4 w-4 text-brand-primary" />
              {t('push.iosStep2')}
            </li>
          </ol>
        </div>
      ) : push.state === 'unsupported' ? (
        <p className="mt-element text-content text-text-secondary">{t('push.unsupported')}</p>
      ) : push.state === 'denied' ? (
        <p className="mt-element text-content text-text-secondary">{deniedText}</p>
      ) : (
        <>
          <p className="mt-element text-content text-text-secondary">
            {push.state === 'on' ? t('push.onIntro') : t('push.offIntro')}
          </p>

          {push.state !== 'on' && (
            <p className="mt-2 text-small text-text-muted">{t('push.privacy')}</p>
          )}

          <div className="mt-element flex flex-wrap gap-inline">
            {push.state === 'on' ? (
              <Button variant="secondary" onClick={() => void onDisable()}>
                {t('push.disable')}
              </Button>
            ) : (
              <Button loading={push.state === 'busy'} onClick={() => void onEnable()}>
                {t('push.enable')}
              </Button>
            )}
          </div>
        </>
      )}

      {message && (
        <p role="alert" className="mt-element text-small text-danger">
          {message}
        </p>
      )}

      {!compact && (devices.data?.items.length ?? 0) > 0 && (
        <div className="mt-section border-t border-border-light pt-element">
          <h3 className="font-heading text-small font-medium text-text-secondary">
            {t('push.devices')}
          </h3>
          <ul className="mt-2 flex flex-col gap-1.5">
            {devices.data?.items.map((device) => (
              <li
                key={device.id}
                className="flex items-center justify-between gap-element text-content text-text-primary"
              >
                <span>
                  {device.deviceLabel || t('push.unknownDevice')}
                  <span className="block text-small text-text-muted">
                    {device.lastSuccessAt
                      ? t('push.lastSuccess', {
                          when: formatDateTime(device.lastSuccessAt),
                        })
                      : t('push.neverUsed')}
                  </span>
                </span>
                {device.disabledAt ? (
                  <Badge tone="neutral">{t('push.deviceOff')}</Badge>
                ) : (
                  <Badge tone="success">{t('push.deviceOn')}</Badge>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
